import { BookOpen, ExternalLink, LifeBuoy, Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type FaqItem = { question: string; answer: string };

export function HelpPage() {
  const { t } = useTranslation('help');
  usePageTitle(t('title'));

  const steps = t('gettingStarted.steps', { returnObjects: true }) as Record<string, string>;
  const faqItems = t('faq.items', { returnObjects: true }) as FaqItem[];
  const docLink = t('documentation.link');
  const contactLink = t('contact.link');

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-3xl font-medium tracking-tight">{t('title')}</h2>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <Rocket className="mb-2 h-6 w-6 text-muted-foreground" />
            <CardTitle>{t('gettingStarted.title')}</CardTitle>
            <CardDescription>{t('gettingStarted.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              {Object.entries(steps).map(([key, label]) => (
                <li key={key}>{label}</li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card className="md:col-span-1">
          <CardHeader>
            <BookOpen className="mb-2 h-6 w-6 text-muted-foreground" />
            <CardTitle>{t('documentation.title')}</CardTitle>
            <CardDescription>{t('documentation.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <a href={docLink} target="_blank" rel="noreferrer">
                {t('documentation.cta')}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>

        <Card className="md:col-span-1">
          <CardHeader>
            <LifeBuoy className="mb-2 h-6 w-6 text-muted-foreground" />
            <CardTitle>{t('contact.title')}</CardTitle>
            <CardDescription>{t('contact.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <a href={contactLink} target="_blank" rel="noreferrer">
                {t('contact.cta')}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('faq.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {faqItems.map((item, i) => (
            <div key={i} className="space-y-1">
              <h4 className="font-medium">{item.question}</h4>
              <p className="text-sm text-muted-foreground">{item.answer}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
